import React, { useState, useEffect } from 'react';
import { Formik } from 'formik';
import {
  Box,
  Panel,
  Radio,
  Label,
  Button,
  Input,
  Link,
  Text,
  theme,
} from '@xometry/xometry_loft';

import { css } from '@emotion/react';
import usersApi from '../../services/users';
import { mb2, ml2 } from '../../utils/styles';
import { GET_ORIGIN } from '../../config';
import { useGetTargetUserData } from '../../contexts/User/UserContext';

const staffSettings = css`
  @media (min-width: 1350px) {
    right: 4%;
    min-width: 248px;
  }
`;

const staffSettings__panel = css`
  background-color: ${theme.colors.yellows[10]};
  border: 1px solid ${theme.colors.yellows[100]};

  @media (min-width: 576px) {
    display: flex;
    flex-wrap: wrap;
  }

  @media (min-width: 1350px) {
    display: block;
    position: fixed;
  }
`;

const staffSettings__item = css`
  flex: 1;
  margin-bottom: 24px;
`;

const staffSettings__panel__title = css`
  flex-basis: 100%;

  display: inline-block;
  font-size: 16px;
  font-weight: 600;
  margin: 5px 0px;
`;

const staffSettings__panel__subtitle = css`
  font-size: 12px;
  font-weight: 600;
`;

const staffSettings__panel__input = css`
  width: 80%;
  ${mb2}
`;

const StaffSettings = ({ userID, displayNotification }) => {
  const [approved3rdPartyShipping, setApproved3rdPartyShipping] =
    useState(false);
  const [registered, setRegistered] = useState('');
  const [isPartner, setIsPartner] = useState('');
  const { userData, isLoading, error } = useGetTargetUserData();

  useEffect(() => {
    if (!isLoading && !error) {
      const {
        approved_3rd_party_shipping: approved3rdPartyShipping,
        first_name,
        legal,
        company_id,
      } = userData;

      const regStatus =
        first_name !== '' && legal && Object.keys(legal).length > 0
          ? 'Complete'
          : 'Incomplete';
      setApproved3rdPartyShipping(approved3rdPartyShipping);
      setRegistered(regStatus);
      setIsPartner(company_id !== 'None' ? 'Yes' : 'No');
    }
  }, [isLoading]);

  const handleShippingClick = (event) => {
    const thirdPartyShipping = event.target.value === 'true';

    const payload = {
      '3rd-party-shipping': thirdPartyShipping,
    };

    usersApi
      .postUserThirdPartyApprovedShipping(userID, payload)
      .then(() => {
        displayNotification('success', '3rd Party Shipping Updated');
        setApproved3rdPartyShipping(thirdPartyShipping);
      })
      .catch(() => {
        displayNotification('danger', '3rd Party Shipping Failed to Save');
      });
  };

  const handleCreditAward = async (values, actions) => {
    const creditAward = parseFloat(values.creditAwardAmount);
    const reason = values.creditAwardReason;
    const payload = {
      credits: creditAward,
      reason,
    };

    usersApi
      .postAwardCredits(userID, payload)
      .then(() => {
        displayNotification('success', `Awarded ${creditAward} Credits`);
      })
      .catch(() => {
        displayNotification('danger', 'Failed to Award Credits');
      });

    actions.setSubmitting(false);
    actions.resetForm({
      values: {
        creditAwardAmount: '',
        creditAwardReason: '',
      },
    });
  };

  if (isLoading || error) return <div />;

  return (
    <div css={staffSettings}>
      <Panel css={staffSettings__panel}>
        <p css={staffSettings__panel__title}>Staff Overrides</p>
        <br />
        <div css={staffSettings__item}>
          <p css={staffSettings__panel__subtitle}>
            Approved for 3rd party shipping?
          </p>
          <div
            css={css`
              display: flex;
            `}
          >
            <div
              css={css`
                ${mb2};
                margin-right: 20px;
              `}
            >
              <Radio
                value
                id="third-party-yes"
                name="third-party"
                checked={approved3rdPartyShipping}
                onChange={handleShippingClick}
              />
              <label css={ml2} htmlFor="third-party-yes">
                Yes
              </label>
            </div>
            <div>
              <Radio
                data-testid="third-party-no"
                value={false}
                id="third-party-no"
                name="third-party"
                checked={!approved3rdPartyShipping}
                onChange={handleShippingClick}
              />
              <label css={ml2} htmlFor="third-party-no">
                No
              </label>
            </div>
          </div>
        </div>
        <div
          title="Whether user is fully registered or not"
          css={staffSettings__item}
        >
          <p css={staffSettings__panel__subtitle}>User Registration Status</p>
          <div>Status: {registered}</div>
        </div>
        <Box
          title="Whether we believe the user is a member of a partner"
          css={staffSettings__item}
        >
          <Text css={staffSettings__panel__subtitle}>Member Of A Partner</Text>
          <Box>
            <Text>{isPartner}</Text>
          </Box>
        </Box>
        <div css={staffSettings__item}>
          <p>
            <b>Award</b>{' '}
            <Link href={`${GET_ORIGIN}/earn-credits/${userID}`}>
              User Credits
            </Link>
          </p>
          <Formik onSubmit={handleCreditAward}>
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
            }) => (
              <form onSubmit={handleSubmit}>
                <Label htmlFor="creditAwardAmount">Amount</Label>
                <Input
                  type="number"
                  id="creditAwardAmount"
                  name="creditAwardAmount"
                  invalid={
                    touched.creditAwardAmount && !!errors.creditAwardAmount
                  }
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.creditAwardAmount || ''}
                  size="large"
                  css={staffSettings__panel__input}
                />
                <Label htmlFor="creditAwardReason">Reason</Label>
                <Input
                  type="text"
                  id="creditAwardReason"
                  name="creditAwardReason"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.creditAwardReason || ''}
                  size="large"
                  css={staffSettings__panel__input}
                />
                <br />
                <Button
                  type="submit"
                  size="large"
                  kind="primary"
                  disabled={isSubmitting}
                >
                  Award
                </Button>
              </form>
            )}
          </Formik>
        </div>
      </Panel>
    </div>
  );
};

export default StaffSettings;
